// Type-only: inventorySlice imports this module at runtime, so a value import
// here would close the cycle.
import type { InventoryItem } from '../redux/inventorySlice';
import type { PantryLearningState } from '../redux/pantryLearningSlice';
import { normalizeIngredientName } from '../services/groceryListService';

/**
 * Duplicate detection for pantry items.
 *
 * The heavy lifting is done by `normalizeIngredientName` (groceryListService),
 * which already strips quantities, parentheses, punctuation, French articles and
 * preparation descriptors ("tranchées", "coupé", "haché"...). That is what makes
 * "nectarines, tranchées" and "nectarines" the same food.
 *
 * This module wraps it with three things it does not do on its own:
 *   1. a per-word plural fold, so "poitrines poulet" meets "poitrine poulet"
 *      (step 9 over there only folds a trailing 's' on the whole string);
 *   2. accent tolerance, for names typed or OCR'd without accents;
 *   3. two guards that stop it from merging foods that are genuinely different
 *      (see PRESERVED_VARIANTS and FORM_MARKERS below).
 */

/**
 * Feminine/plural spellings of the descriptors that groceryListService marks as
 * meaningful (PRESERVE_DESCRIPTORS) but only lists in the singular. Without this
 * pass "tomates séchées" normalizes all the way down to "tomato", and dried
 * tomatoes would silently merge into fresh ones.
 */
const PRESERVED_VARIANTS: Record<string, string> = {
  'séchée': 'séché', 'séchés': 'séché', 'séchées': 'séché',
  'congelée': 'congelé', 'congelés': 'congelé', 'congelées': 'congelé',
  'entière': 'entier', 'entiers': 'entier', 'entières': 'entier',
  'moulue': 'moulu', 'moulus': 'moulu', 'moulues': 'moulu',
  'écrasée': 'écrasé', 'écrasés': 'écrasé', 'écrasées': 'écrasé',
  'fraîches': 'fraîche',
};

/**
 * Words that change what a food *is*, not just how it was cut.
 * `normalizeIngredientName` strips these ("Jus de lime" -> "lime",
 * "garlic powder" -> "garlic"), which is right for matching a recipe against a
 * pantry but wrong for merging two pantry rows. When one is present we tag the
 * key so the derived form can never collapse into the whole food.
 */
const FORM_MARKERS: Array<{ marker: string; pattern: RegExp }> = [
  { marker: 'juice', pattern: /\b(jus|juice)\b/ },
  { marker: 'zest', pattern: /\b(zeste|zest)\b/ },
  { marker: 'powder', pattern: /\b(poudre|powder)\b/ },
  { marker: 'puree', pattern: /\b(puree|pureed)\b/ },
];

function stripAccents(text: string): string {
  // NFD does not decompose the œ/æ ligatures, and the fold below drops anything
  // outside [a-z0-9] — without this "Œufs" would collapse to "uf".
  return text
    .replace(/œ/gi, 'oe')
    .replace(/æ/gi, 'ae')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

/** Accent-insensitive lookup table for PRESERVED_VARIANTS. */
const PRESERVED_VARIANT_LOOKUP: Record<string, string> = Object.entries(
  PRESERVED_VARIANTS
).reduce((acc, [variant, canonical]) => {
  acc[variant] = canonical;
  acc[stripAccents(variant)] = canonical;
  return acc;
}, {} as Record<string, string>);

/**
 * Rewrite preserved descriptors to the singular spelling groceryListService
 * recognises, so they survive normalization instead of being stripped.
 */
function keepMeaningfulDescriptors(text: string): string {
  return text
    .split(/(\s+)/)
    .map(token => {
      const bare = token.toLowerCase().replace(/[.,;]/g, '');
      return PRESERVED_VARIANT_LOOKUP[bare] ?? token;
    })
    .join('');
}

/**
 * Final fold applied after normalization: drop accents and leftover punctuation,
 * then remove a trailing 's' from each word.
 */
function fold(text: string): string {
  return stripAccents(text.toLowerCase())
    .replace(/[^a-z0-9%\s-]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map(word => (word.length > 1 && word.endsWith('s') ? word.slice(0, -1) : word))
    .join(' ')
    .trim();
}

function detectFormMarkers(name: string): string[] {
  const haystack = stripAccents(name.toLowerCase());
  return FORM_MARKERS.filter(({ pattern }) => pattern.test(haystack)).map(m => m.marker);
}

/**
 * Build the set of normalized keys for a name. Two names are the same food when
 * their key sets intersect.
 *
 * Two keys rather than one because `normalizeIngredientName` carries a
 * French->English table: "Tomates" folds to "tomato" but the accented misspelling
 * "Tomàtes" misses the table and stays "tomàte". Normalizing the accent-stripped
 * spelling as well lets the two meet on "tomato".
 */
export function duplicateKeys(name: string): Set<string> {
  const trimmed = (name ?? '').trim();
  if (!trimmed) return new Set();

  const suffix = detectFormMarkers(trimmed)
    .map(marker => ` [${marker}]`)
    .join('');

  const keys = new Set<string>();
  for (const variant of [trimmed, stripAccents(trimmed)]) {
    const key = fold(normalizeIngredientName(keepMeaningfulDescriptors(variant)));
    if (key) keys.add(`${key}${suffix}`);
  }

  return keys;
}

function keysIntersect(a: Set<string>, b: Set<string>): boolean {
  for (const key of a) {
    if (b.has(key)) return true;
  }
  return false;
}

/** True when both names refer to the same food (ignoring how it was prepared). */
export function isSameFood(a: string, b: string): boolean {
  const keysA = duplicateKeys(a);
  if (keysA.size === 0) return false;
  return keysIntersect(keysA, duplicateKeys(b));
}

const isActive = (item: InventoryItem): boolean => !item.status || item.status === 'active';

/**
 * Find a duplicate item in the inventory.
 * Returns the first active inventory item that is the same food, or undefined.
 *
 * Examples:
 * - 'Lait 2 %' matches 'lait 2 %' (case-insensitive)
 * - 'Tomates' matches 'Tomate' (plural tolerance)
 * - 'nectarines, tranchées' matches 'nectarines' (preparation is not identity)
 * - 'Tomates séchées' does NOT match 'Tomates' (dried is a different food)
 * - 'Jus de lime' does NOT match 'Lime' (juice is a different food)
 * - Only matches active items (status === 'active' or status undefined)
 */
export function findDuplicate(name: string, items: InventoryItem[]): InventoryItem | undefined {
  const keys = duplicateKeys(name);
  if (keys.size === 0) return undefined;

  return items.find(item => isActive(item) && keysIntersect(keys, duplicateKeys(item.name)));
}

/**
 * Bucket active inventory items that refer to the same food.
 * Only groups of two or more are returned — those are the ones worth merging.
 * Order is stable: groups follow first appearance, members follow inventory order.
 *
 * Delegates to `findDuplicateGroupsLearned` with no lessons rather than keeping
 * a second copy of the bucketing. Two implementations of "is this the same
 * food" is exactly how the pantry ended up with a strong matcher on one write
 * path and a weak one on the other.
 */
export function findDuplicateGroups(items: InventoryItem[]): InventoryItem[][] {
  return findDuplicateGroupsLearned(items);
}

export interface DuplicateMergePlan {
  /** The row that stays, carrying the merged quantity. */
  survivor: InventoryItem;
  /** The rows folded into the survivor and then removed. */
  absorbed: InventoryItem[];
  /** Fields to write onto the survivor. Empty when nothing changes. */
  updates: Partial<InventoryItem>;
}

/** Sortable expiry; items with no known date sort last. */
function expiryRank(item: InventoryItem): number {
  const e = item.expiryDate;
  if (!e) return Number.POSITIVE_INFINITY;
  return e.year * 10000 + e.month * 100 + e.day;
}

/**
 * Decide how to collapse a group of duplicates into one row.
 *
 * The survivor is the row expiring soonest, so a merged row never claims more
 * freshness than the food actually has — sliced nectarines really do go off
 * before the whole ones sitting beside them.
 *
 * It then takes the shortest name in the group. A preparation variant is the
 * base food plus a descriptor, so the shortest name is the plain food: merging
 * "nectarines" into "nectarines, tranchées" should leave "nectarines", not the
 * other way round.
 *
 * Quantities are only summed when every member is measured the same way.
 * Adding "2" to "500 g" would be nonsense, so in that case the survivor keeps
 * its own quantity.
 */
export function planDuplicateMerge(group: InventoryItem[]): DuplicateMergePlan {
  const survivor = group.reduce((best, item) =>
    expiryRank(item) < expiryRank(best) ? item : best
  );
  const absorbed = group.filter(item => item.id !== survivor.id);

  const updates: Partial<InventoryItem> = {};

  const clearestName = group.reduce((best, item) =>
    item.name.trim().length < best.name.trim().length ? item : best
  ).name;
  if (clearestName !== survivor.name) {
    updates.name = clearestName;
  }

  // Only sum when the members are measured the same way *and* at least one of
  // them actually declares a quantity. Two rows that both say nothing about
  // amount should stay silent about it rather than merge into a made-up "2".
  const units = new Set(group.map(item => item.unit ?? ''));
  const anyQuantityKnown = group.some(item => typeof item.quantity === 'number');

  if (units.size === 1 && anyQuantityKnown) {
    const total = group.reduce((sum, item) => sum + (item.quantity ?? 1), 0);
    if (total !== survivor.quantity) {
      updates.quantity = total;
    }
  }

  return { survivor, absorbed, updates };
}


/* ------------------------------------------------------------------------- *
 * What the user has taught us
 *
 * The rules above are the app's opinion. These functions let the user's own
 * confirmations and rejections override it, so the matcher gets better at this
 * particular kitchen instead of staying wrong in the same way forever.
 * ------------------------------------------------------------------------- */

/** The lessons, in the shape the matcher needs. Optional everywhere. */
export type Learning = Pick<PantryLearningState, 'aliases' | 'distinct'>;

/**
 * Stable identity for a name in the lesson store.
 *
 * Deliberately NOT `duplicateKeys`. That collapses descriptors, which is right
 * for finding duplicates and fatal for recording that two rows are different:
 * "vin blanc" and "vin rouge" both reduce to "vin", so a rejection keyed on it
 * would say "vin is not vin" and mean nothing.
 *
 * This folds only what is never meaningful — case, accents, punctuation and a
 * trailing plural — so a lesson survives the row being retyped, but two names
 * the user has told apart stay apart.
 */
export function primaryKey(name: string): string {
  return fold(name ?? '');
}

/**
 * Resolve confirmed aliases transitively: if the user merged a into b and b
 * into c, then a and c are the same food too. Plain union-find over the key
 * space — small enough that rebuilding it per call costs nothing.
 */
function buildAliasResolver(learning?: Learning): (key: string) => string {
  if (!learning?.aliases?.length) return (key) => key;

  const parent = new Map<string, string>();
  const find = (key: string): string => {
    let root = key;
    while (parent.has(root) && parent.get(root) !== root) root = parent.get(root)!;
    return root;
  };

  for (const { a, b } of learning.aliases) {
    if (!a || !b) continue;
    if (!parent.has(a)) parent.set(a, a);
    if (!parent.has(b)) parent.set(b, b);
    const rootA = find(a);
    const rootB = find(b);
    if (rootA !== rootB) parent.set(rootA, rootB);
  }

  return (key) => (parent.has(key) ? find(key) : key);
}

function buildDistinctSet(learning?: Learning): Set<string> {
  const set = new Set<string>();
  for (const { a, b } of learning?.distinct ?? []) {
    if (a && b) set.add([a, b].sort().join(' | '));
  }
  return set;
}

const pairKey = (a: string, b: string) => [a, b].sort().join(' | ');

/**
 * True when both names are the same food, taking the user's corrections into
 * account. A rejection always wins: the user looking at two rows and saying
 * "these are different" is better evidence than any string rule.
 */
export function isSameFoodLearned(a: string, b: string, learning?: Learning): boolean {
  const keyA = primaryKey(a);
  const keyB = primaryKey(b);
  if (!keyA || !keyB) return false;

  if (buildDistinctSet(learning).has(pairKey(keyA, keyB))) return false;

  const resolve = buildAliasResolver(learning);
  if (resolve(keyA) === resolve(keyB)) return true;

  return isSameFood(a, b);
}

/**
 * Bucket active rows that are the same food, honouring the user's corrections.
 *
 * Unlike `findDuplicateGroups`, a rejected pair is never grouped again, and a
 * pair the user confirmed is grouped even when the string rules disagree.
 */
export function findDuplicateGroupsLearned(
  items: InventoryItem[],
  learning?: Learning
): InventoryItem[][] {
  const distinct = buildDistinctSet(learning);
  const resolve = buildAliasResolver(learning);

  const buckets: Array<{ keys: Set<string>; members: InventoryItem[] }> = [];

  for (const item of items) {
    if (!isActive(item)) continue;
    // A row that came from a delivery is a lot, not a duplicate — see
    // findDuplicateGroups for why folding it in would lose its arrival date.
    if (item.importId) continue;

    const keys = duplicateKeys(item.name);
    if (keys.size === 0) continue;
    const itemKey = primaryKey(item.name);

    const bucket = buckets.find((candidate) => {
      // Never regroup a pair the user has separated.
      if (candidate.members.some(m => distinct.has(pairKey(primaryKey(m.name), itemKey)))) {
        return false;
      }
      if (keysIntersect(candidate.keys, keys)) return true;
      return candidate.members.some(m => resolve(primaryKey(m.name)) === resolve(itemKey));
    });

    if (bucket) {
      bucket.members.push(item);
      keys.forEach(key => bucket.keys.add(key));
    } else {
      buckets.push({ keys, members: [item] });
    }
  }

  return buckets.filter(b => b.members.length > 1).map(b => b.members);
}

/** Tokens of a normalized key, minus the `[juice]`-style form markers. */
function keyTokens(name: string): string[] {
  return primaryKey(name)
    .replace(/\[[a-z]+\]/g, '')
    .split(' ')
    .filter(token => token.length > 2);
}

/**
 * Pairs that *might* be the same food but are not certain enough to merge.
 *
 * `findDuplicateGroups` only groups on an exact key collision, which is why it
 * found four groups in a 233-row pantry that visibly held far more. This casts
 * a wider net and hands the result to the user rather than acting on it.
 *
 * The net is cast on *discriminative* words only. Matching on any shared token
 * produced groups like "Fleurons de Brocoli Surgelés" with "Pois Petits
 * Surgelés" — they share "surgelés", which says how five different foods are
 * stored and nothing about what they are. A word that appears all over the
 * pantry cannot be evidence that two rows are the same thing, so a token only
 * counts when it is rare in this particular pantry.
 *
 * Tuned for precision over recall: a review queue full of wrong guesses is one
 * the user stops opening, and a missed pair merely stays two rows.
 */
const MAX_DISCRIMINATIVE_FREQUENCY = 2;

export function findMergeCandidates(
  items: InventoryItem[],
  learning?: Learning
): InventoryItem[][] {
  const distinct = buildDistinctSet(learning);
  const certain = new Set(
    findDuplicateGroupsLearned(items, learning).flat().map(item => item.id)
  );

  const active = items.filter(item => isActive(item) && !item.importId && !certain.has(item.id));

  // How many rows each token appears in — a token's document frequency.
  const frequency = new Map<string, number>();
  const tokensById = new Map<string, string[]>();
  for (const item of active) {
    const tokens = [...new Set(keyTokens(item.name))];
    tokensById.set(item.id, tokens);
    for (const token of tokens) frequency.set(token, (frequency.get(token) ?? 0) + 1);
  }

  const groups: InventoryItem[][] = [];
  const placed = new Set<string>();

  for (let i = 0; i < active.length; i++) {
    if (placed.has(active[i].id)) continue;
    const tokensA = tokensById.get(active[i].id) ?? [];
    if (tokensA.length === 0) continue;

    const group = [active[i]];

    for (let j = i + 1; j < active.length; j++) {
      if (placed.has(active[j].id)) continue;
      if (distinct.has(pairKey(primaryKey(active[i].name), primaryKey(active[j].name)))) continue;

      const tokensB = tokensById.get(active[j].id) ?? [];
      if (tokensB.length === 0) continue;

      const shared = tokensA.filter(token => tokensB.includes(token));
      const discriminative = shared.filter(
        token => (frequency.get(token) ?? 0) <= MAX_DISCRIMINATIVE_FREQUENCY
      );
      if (discriminative.length === 0) continue;

      group.push(active[j]);
      placed.add(active[j].id);
    }

    if (group.length > 1) {
      placed.add(active[i].id);
      groups.push(group);
    }
  }

  return groups;
}
